import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatLocalDateOnly } from "../../shared utils/dateFormatters";

const DISCOUNT_PREFIX = "DISCOUNT:";

const isDiscountPayment = (p) =>
  String(p?.paymentAccount || "").toUpperCase().startsWith(DISCOUNT_PREFIX);

const parseDiscountReason = (paymentAccount) => {
  const s = String(paymentAccount || "").trim();
  if (!s.toUpperCase().startsWith(DISCOUNT_PREFIX)) return "";
  return s.slice(DISCOUNT_PREFIX.length).trim();
};

/**
 * Generate a combined invoice PDF for multiple groups.
 * All line items in one table, all payments in one table.
 */
export function generateCombinedInvoicePDF({
  groupNames,
  memberCount,
  invoiceLines,
  payments,
  discountPayments = [],
  discountTotal,
  paidExcludingDiscounts,
  invoiceTotal,
  balance,
  gross = true,
  headerNote = "",
}) {
  const brandNavy = [26, 31, 113];
  const lightGrey = [245, 245, 245];
  const logo = process.env.PUBLIC_URL + "/assets/img/blissLogo.png";
  const footerImg = process.env.PUBLIC_URL + "/assets/img/footer.png";

  const tableDefaults = {
    theme: "grid",
    margin: { left: 40, right: 40 },
    styles: { fontSize: 10, cellPadding: 6, textColor: 40, lineColor: [180, 180, 180], lineWidth: 0.5 },
    headStyles: { fillColor: brandNavy, textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: lightGrey },
  };

  const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const today = formatLocalDateOnly(new Date());
  let y = 40;

  // Header
  const logoWidth = 50;
  const logoHeight = 60;
  pdf.addImage(logo, "PNG", 40, y, logoWidth, logoHeight);

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(20);
  pdf.setTextColor(...brandNavy);
  pdf.text(
    paidExcludingDiscounts === 0 ? "Invoice" : "Receipt",
    pageWidth - 40,
    y + logoHeight / 2 + 8,
    { align: "right" }
  );

  const lineY = y + logoHeight + 10;
  pdf.setDrawColor(...brandNavy);
  pdf.setLineWidth(1);
  pdf.line(40, lineY, pageWidth - 40, lineY);
  y = lineY + 18;

  pdf.setFontSize(11);
  pdf.setTextColor(40);
  pdf.text(`Groups: ${groupNames}`, 40, y);
  pdf.text(`Date: ${today}`, pageWidth - 40, y, { align: "right" });
  y += 16;
  pdf.text(`Members: ${memberCount || 0}`, 40, y);
  y += 18;

  // Optional free-text header block (e.g. school/agent name & address, VAT number)
  const noteText = (headerNote || "").trim();
  if (noteText) {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.setTextColor(40);
    const noteLines = pdf.splitTextToSize(noteText, pageWidth - 80);
    pdf.text(noteLines, 40, y);
    y += noteLines.length * 13 + 12;
  }

  // Invoice lines table
  autoTable(pdf, {
    ...tableDefaults,
    startY: y,
    head: [["Qty", "Product", "Start", "End", "Unit", "Total"]],
    body:
      (invoiceLines || []).length > 0
        ? invoiceLines.map((line) => {
            const lineTotal = gross ? line.grossTotal : line.netTotal;
            return [
              String(line.qty),
              line.productName || "",
              line.startDate ? formatLocalDateOnly(new Date(line.startDate)) : "",
              line.endDate ? formatLocalDateOnly(new Date(line.endDate)) : "",
              `£ ${Number(line.rate || 0).toFixed(2)}`,
              `£ ${Number(lineTotal || 0).toFixed(2)}`,
            ];
          })
        : [["", "No bookings", "", "", "", ""]],
    columnStyles: {
      0: { halign: "right", cellWidth: 40 },
      1: { cellWidth: "auto" },
      2: { cellWidth: 70 },
      3: { cellWidth: 70 },
      4: { halign: "right", cellWidth: 70 },
      5: { halign: "right", cellWidth: 80 },
    },
  });

  y = pdf.lastAutoTable.finalY + 18;

  // Payments table
  if (y > pageHeight - 220) { pdf.addPage(); y = 40; }

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12);
  pdf.setTextColor(...brandNavy);
  pdf.text("Payments", pageWidth / 2, y, { align: "center" });
  y += 8;

  autoTable(pdf, {
    ...tableDefaults,
    startY: y,
    head: [["Date", "Notes", "Amount"]],
    body:
      (payments || []).length > 0
        ? payments.map((p) => {
            const discount = isDiscountPayment(p);
            const label = discount
              ? p.paymentAccount || ""
              : `PAYMENT${p.notes ? `: ${p.notes}` : ""}`;
            return [
              p.paymentDate || "",
              label,
              `${discount ? "-£" : "£"} ${Number(p.paymentAmount || 0).toFixed(2)} GBP`,
            ];
          })
        : [["No payments recorded", "", ""]],
  });

  y = pdf.lastAutoTable.finalY + 18;

  // Totals
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12);
  pdf.setTextColor(...brandNavy);
  pdf.text("Totals", pageWidth / 2, y, { align: "center" });
  y += 8;

  const totalsBody = [
    [`Total (${gross ? "Gross" : "Net"})`, `£ ${invoiceTotal.toFixed(2)} GBP`],
  ];

  (discountPayments || []).forEach((p) => {
    const reason = parseDiscountReason(p.paymentAccount) || "no reason";
    const amt = Number(p.paymentAmount) || 0;
    totalsBody.push([`Discount — ${reason}`, `-£ ${amt.toFixed(2)} GBP`]);
  });

  totalsBody.push([
    "Payments received",
    paidExcludingDiscounts > 0 ? `-£ ${paidExcludingDiscounts.toFixed(2)} GBP` : "£ 0.00 GBP",
  ]);
  totalsBody.push(["Balance due", `£ ${balance.toFixed(2)} GBP`]);

  autoTable(pdf, {
    ...tableDefaults,
    startY: y,
    head: [["Summary", "Amount"]],
    body: totalsBody,
    columnStyles: {
      0: { cellWidth: 340, fontStyle: "bold" },
      1: { cellWidth: "auto", halign: "right" },
    },
  });

  // Footer
  const finalPage = pdf.getNumberOfPages();
  pdf.setPage(finalPage);
  pdf.setDrawColor(...brandNavy);
  pdf.setLineWidth(1);
  pdf.line(40, pageHeight - 70, pageWidth - 40, pageHeight - 70);
  pdf.addImage(footerImg, "PNG", (pageWidth - 120) / 2, pageHeight - 55, 120, 35);

  const fileSlug = (groupNames || "combined")
    .replace(/[^a-z0-9]+/gi, "_")
    .toLowerCase()
    .slice(0, 60);
  pdf.save(`combined_invoice_${fileSlug}_${today}.pdf`);
}
